import { useCallback, useMemo, useState } from 'react';

import { ChevronDown } from '@mint-lib/icons';
import {
  Chip,
  Divider,
  itemVariants,
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';
import { format as formatDate, set as setDate } from 'date-fns';

export type Props = {
  value?: Date | null;
  onChange?: (time: Date | null) => void;
  minuteStep?: number;
  placeholder?: string;
  isDisabled?: boolean;
};

function getStartOfToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
}

const ChipTimepicker = ({
  value,
  onChange,
  minuteStep: minuteStepProp = 15,
  placeholder = 'Select time',
  isDisabled = false,
}: Props) => {
  const minuteStep = useMemo(() => {
    if (!minuteStepProp || minuteStepProp <= 0) return 15;
    if (minuteStepProp > 60) return 60;
    return minuteStepProp;
  }, [minuteStepProp]);

  const [open, setOpen] = useState(false);

  const hoursOptions = useMemo(
    () => Array.from({ length: 24 }, (_, i) => i),
    [],
  );

  const minutesOptions = useMemo(() => {
    const mins: number[] = [];
    for (let m = 0; m < 60; m += minuteStep) mins.push(m);
    return mins;
  }, [minuteStep]);

  const displayText = useMemo(() => {
    if (!value) return '';

    return formatDate(value, 'HH:mm');
  }, [value]);

  const getBaseDate = useCallback(() => value ?? getStartOfToday(), [value]);

  const commit = useCallback(
    (next: Date) => {
      onChange?.(next);
    },
    [onChange],
  );

  const handleSelectHour = useCallback(
    (hour: number) => {
      const next = setDate(getBaseDate(), { hours: hour });
      commit(next);
    },
    [getBaseDate, commit],
  );

  const handleSelectMinute = useCallback(
    (minute: number) => {
      const next = setDate(getBaseDate(), {
        minutes: minute,
        seconds: 0,
        milliseconds: 0,
      });
      commit(next);
    },
    [getBaseDate, commit],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Chip
          color={value ? 'tertiary' : 'secondary'}
          variant={value ? 'filled' : 'outline'}
          size="large"
          disabled={isDisabled}
        >
          {displayText || placeholder}
          <ChevronDown />
        </Chip>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="z-popover overflow-hidden"
        >
          <div className="flex max-h-56">
            <ul className="max-h-56 w-fit [scrollbar-width:none] overflow-auto [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {hoursOptions.map((h) => {
                const isSelected = getBaseDate().getHours() === h;
                const label = h < 10 ? `0${h}` : `${h}`;

                return (
                  <li key={`h-${h}`}>
                    <button
                      type="button"
                      className={cn(
                        itemVariants({
                          popoverItem: isSelected ? 'selected' : 'default',
                        }),
                        typographyVariants({ variant: 'bodyShort01' }),
                        'w-full',
                      )}
                      onClick={() => handleSelectHour(h)}
                    >
                      {label}
                    </button>
                  </li>
                );
              })}
            </ul>
            <Divider orientation="vertical" className="max-h-56" />
            <ul className="max-h-56 w-fit [scrollbar-width:none] overflow-auto [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {minutesOptions.map((m) => {
                const isSelected = getBaseDate().getMinutes() === m;
                const label = m < 10 ? `0${m}` : `${m}`;
                return (
                  <li key={`m-${m}`}>
                    <button
                      type="button"
                      className={cn(
                        itemVariants({
                          popoverItem: isSelected ? 'selected' : 'default',
                        }),
                        typographyVariants({ variant: 'bodyShort01' }),
                        'w-full',
                      )}
                      onClick={() => handleSelectMinute(m)}
                    >
                      {label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};

export { ChipTimepicker };
